import { useEffect, useState } from 'react';
import { toast } from 'material-react-toastify';
import {
  Box,
  Button,
  Divider,
  Paper,
  Skeleton,
  TextField,
  Typography,
} from '@mui/material';
import { MetaTable } from '../../interfaces/Meta/MetaTable';
import { getEmptyMeta } from '../../interfaces/Meta/Meta';
import { getMeta, updateMeta } from '../../api/Meta';
import { sharedStyles } from '../../utils/Style';
import { getFeURL } from '../../api/Settings';
import { TranslationAdornment } from '../Translation/TranslationAdornment';

interface MetaCardProps {
  table: MetaTable;
  id: number;
}

export const MetaCard = (props: MetaCardProps) => {
  const [loading, setLoading] = useState(false);
  const [meta, setMeta] = useState(getEmptyMeta());
  const [feURL, setFeURL] = useState('');

  useEffect(() => {
    setLoading(true);
    getMeta(props.table, props.id)
      .then((m) => setMeta(m))
      .catch((err) => toast.error(err.message))
      .finally(() => setLoading(false));

    getFeURL()
      .then((url) => setFeURL(url))
      .catch((err) => toast.error(err.message));
  }, []);

  const handleSave = () => {
    setLoading(true);
    updateMeta(props.table, props.id, meta)
      .then((m) => {
        toast.success('Meta alanları başarıyla güncellendi');
        setMeta(m);
      })
      .catch((err) => toast.error(err.message))
      .finally(() => setLoading(false));
  };

  return (
    <Paper
      sx={{
        display: 'flex',
        flexDirection: 'column',
        width: 400,
        height: 'fit-content',
        p: 3,
        gap: 1.5,
      }}
    >
      <Typography variant='h6'>Meta</Typography>
      <Divider sx={{ mt: -1 }} />

      {loading ? (
        <>
          <Skeleton variant='rectangular' width='100%' height={30} />
          <Skeleton variant='rectangular' width='100%' height={60} />
          <Skeleton variant='rectangular' width='100%' height={30} />
        </>
      ) : (
        <>
          <TextField
            label='Başlık'
            variant='outlined'
            size='small'
            fullWidth
            value={meta.title ?? ''}
            onChange={(e) =>
              setMeta({ ...meta, title: e.target.value || undefined })
            }
            InputProps={
              meta.title
                ? {
                    endAdornment: (
                      <TranslationAdornment
                        multiline
                        defaultValue={meta.title}
                        value={meta.titleTranslation}
                        onChange={(x) =>
                          setMeta({ ...meta, titleTranslation: x })
                        }
                      />
                    ),
                  }
                : undefined
            }
          />

          <TextField
            label='Açıklama'
            variant='outlined'
            size='small'
            fullWidth
            multiline
            value={meta.description ?? ''}
            onChange={(e) =>
              setMeta({ ...meta, description: e.target.value || undefined })
            }
            InputProps={
              meta.description
                ? {
                    endAdornment: (
                      <TranslationAdornment
                        multiline
                        defaultValue={meta.description}
                        value={meta.descriptionTranslation}
                        onChange={(x) =>
                          setMeta({ ...meta, descriptionTranslation: x })
                        }
                      />
                    ),
                  }
                : undefined
            }
          />

          <TextField
            label='Canonical URL'
            variant='outlined'
            size='small'
            fullWidth
            value={meta.canonicalURL ?? ''}
            onChange={(e) =>
              setMeta({ ...meta, canonicalURL: e.target.value || undefined })
            }
            helperText={
              meta.canonicalURL
                ? feURL
                  ? `${feURL}/${meta.canonicalURL}`
                  : undefined
                : 'Varsayılan'
            }
          />
        </>
      )}

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'end',
        }}
      >
        <Button
          disabled={loading}
          variant='contained'
          onClick={handleSave}
          sx={{ ...sharedStyles.buttonText }}
        >
          Kaydet
        </Button>
      </Box>
    </Paper>
  );
};
